import type { WorkspaceMode, StudioRoute, ViewMode, ViewportSize, CanvasView, IdeTab, HomeTab, PreviewTarget } from './types'
import { resolveStudioRouteFromPathname } from '@/lib/studio-route-map'
import { applianceHidesRoute } from '@/services/appliance-commerce-surface'

/**
 * The screen the ADDRESS names, resolved before the first render.
 *
 * `studioRoute` used to start at 'home' unconditionally and only be corrected
 * by an effect in App.tsx — one commit later. Every `/studio/<screen>` deep
 * link therefore mounted Home first, ran its mount effects (a `/api/projects`
 * read among them), and only then swapped in the screen the user asked for.
 * The wasted read landed on every screen in the product, authenticated or
 * not. Unresolvable addresses still fall back to 'home', exactly as before,
 * and App.tsx's sync still runs — it now agrees with this value instead of
 * correcting it.
 */
function initialStudioRoute(): StudioRoute {
  if (typeof window === 'undefined') return 'home'
  const path = window.location.pathname
  if (!path.startsWith('/studio')) return 'home'
  return resolveStudioRouteFromPathname(path) ?? 'home'
}

export interface UiSlice {
  mode: WorkspaceMode
  setMode: (mode: WorkspaceMode) => void
  chatPanelOpen: boolean
  navPanelOpen: boolean
  inspectorPanelOpen: boolean
  diagnosticPanelOpen: boolean
  devtoolsPanelOpen: boolean
  toggleChatPanel: () => void
  toggleNavPanel: () => void
  toggleInspectorPanel: () => void
  toggleDiagnosticPanel: () => void
  toggleDevToolsPanel: () => void
  setChatPanelOpen: (open: boolean) => void
  setNavPanelOpen: (open: boolean) => void
  setInspectorPanelOpen: (open: boolean) => void
  setDiagnosticPanelOpen: (open: boolean) => void
  setDevToolsPanelOpen: (open: boolean) => void
  closeAllPanels: () => void
  bottomBarExpanded: boolean
  navRailExpanded: boolean
  toggleBottomBar: () => void
  toggleNavRail: () => void
  setBottomBarExpanded: (expanded: boolean) => void
  setNavRailExpanded: (expanded: boolean) => void
  studioRoute: StudioRoute
  setStudioRoute: (route: StudioRoute) => void
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
  chatSidebarOpen: boolean
  toggleChatSidebar: () => void
  setChatSidebarOpen: (open: boolean) => void
  viewport: ViewportSize
  setViewport: (vp: ViewportSize) => void
  /**
   * The preview device the user picked, per project id.
   *
   * Persisted (see `UI_PERSIST_KEYS` in `studio-store.ts`) because it is a
   * device preference, not content — the same reason `cogo-studio-ui-v1`
   * survives sign-out. Keyed by project because the alternative, one global
   * device, makes every later project open in the frame of whichever project
   * the user last touched, which is the defect this field exists to end.
   */
  previewDeviceChoices: Record<string, ViewportSize>
  setPreviewDeviceChoice: (projectId: string | null | undefined, viewport: ViewportSize) => void
  canvasView: CanvasView
  setCanvasView: (view: CanvasView) => void
  ideActiveTab: IdeTab
  setIdeActiveTab: (tab: IdeTab) => void
  slideOutOpen: boolean
  setSlideOutOpen: (open: boolean) => void
  selectedElementId: string | null
  setSelectedElementId: (id: string | null) => void
  currentPageId: string | null
  setCurrentPageId: (id: string | null) => void
  homeTab: HomeTab
  setHomeTab: (tab: HomeTab) => void
  previewTarget: PreviewTarget
  setPreviewTarget: (target: PreviewTarget) => void
  // Panel sizes — inline branch of ResponsivePanel (xl+ for chat/inspector,
  // viewport-height-driven for devtools). Pixel values. Clamped by the
  // drag handle to the min/max documented on each setter.
  chatPanelWidth: number
  inspectorPanelWidth: number
  devtoolsPanelHeight: number
  setChatPanelWidth: (width: number) => void
  setInspectorPanelWidth: (width: number) => void
  setDevtoolsPanelHeight: (height: number) => void
  // Phase 3 — IDE rail Actions section owns Publish / Deploy modals. State
  // lives in the store so the rail trigger and the modal share the same
  // open/close flag without prop drilling through the layout.
  publishModalOpen: boolean
  setPublishModalOpen: (open: boolean) => void
  deployModalOpen: boolean
  setDeployModalOpen: (open: boolean) => void
}

type Set = (fn: (state: UiSlice) => void) => void

/** Bound on the per-project device-choice map so it cannot grow without limit. */
const MAX_PREVIEW_DEVICE_CHOICES = 50

const CHAT_PANEL_MIN_WIDTH = 280
export const CHAT_PANEL_DEFAULT_WIDTH = 320
const CHAT_PANEL_MAX_WIDTH = 480

export function createUiSlice(
  set: Set,
  _get: unknown,
  _api: unknown,
): UiSlice {
  return {
    mode: 'chat',
    setMode: (mode) => set((s) => { s.mode = mode }),
    chatPanelOpen: true,
    navPanelOpen: false,
    inspectorPanelOpen: false,
    diagnosticPanelOpen: false,
    devtoolsPanelOpen: false,
    toggleChatPanel: () => set((s) => { s.chatPanelOpen = !s.chatPanelOpen }),
    toggleNavPanel: () => set((s) => { s.navPanelOpen = !s.navPanelOpen }),
    toggleInspectorPanel: () => set((s) => { s.inspectorPanelOpen = !s.inspectorPanelOpen }),
    toggleDiagnosticPanel: () => set((s) => { s.diagnosticPanelOpen = !s.diagnosticPanelOpen }),
    toggleDevToolsPanel: () => set((s) => { s.devtoolsPanelOpen = !s.devtoolsPanelOpen }),
    setChatPanelOpen: (open) => set((s) => { s.chatPanelOpen = open }),
    setNavPanelOpen: (open) => set((s) => { s.navPanelOpen = open }),
    setInspectorPanelOpen: (open) => set((s) => { s.inspectorPanelOpen = open }),
    setDiagnosticPanelOpen: (open) => set((s) => { s.diagnosticPanelOpen = open }),
    setDevToolsPanelOpen: (open) => set((s) => { s.devtoolsPanelOpen = open }),
    closeAllPanels: () => set((s) => {
      s.chatPanelOpen = false
      s.navPanelOpen = false
      s.inspectorPanelOpen = false
      s.diagnosticPanelOpen = false
      s.devtoolsPanelOpen = false
      s.slideOutOpen = false
    }),
    bottomBarExpanded: true,
    // Expanded is the shipped default and has been since 25409aefa
    // (2026-07-08), which pinned the rail's `effectiveExpanded` to the literal
    // `true`. This field said `false` throughout — harmlessly, because nothing
    // read it. Now that `creator-nav-panel.tsx` reads it again, the declared
    // default has to agree with what the rail actually renders, or restoring
    // the control would collapse every rail on first boot.
    navRailExpanded: true,
    toggleBottomBar: () => set((s) => { s.bottomBarExpanded = !s.bottomBarExpanded }),
    toggleNavRail: () => set((s) => { s.navRailExpanded = !s.navRailExpanded }),
    setBottomBarExpanded: (expanded) => set((s) => { s.bottomBarExpanded = expanded }),
    setNavRailExpanded: (expanded) => set((s) => { s.navRailExpanded = expanded }),
    studioRoute: initialStudioRoute(),
    // Slice 6 §3.2 (2026-08-18): on the appliance the withheld commerce
    // screens are gone from the ADDRESSING layer while the RENDER pathway is
    // intact end to end (bundled component → registered island → hand-written
    // block condition in blocks-v2/studio/dashboard/ui.cogo.json). The only
    // thing between a withheld screen and the user was that no CURRENT
    // writer of `studioRoute` accepted those values — a closure resting on
    // every FUTURE caller remembering an allowlist that lives in a different
    // file from the block condition. So the refusal lives HERE, at the one
    // sink every writer passes through. The warn keeps the refusal from
    // being the silent kind this project keeps paying for.
    setStudioRoute: (route) => {
      if (applianceHidesRoute(route)) {
        console.warn(
          `[studio] setStudioRoute("${route}") refused: route is withheld on this distribution (slice 6 §3.2).`,
        )
        return
      }
      set((s) => { s.studioRoute = route })
    },
    viewMode: 'preview',
    setViewMode: (mode) => set((s) => { s.viewMode = mode }),
    chatSidebarOpen: true,
    toggleChatSidebar: () => set((s) => { s.chatSidebarOpen = !s.chatSidebarOpen }),
    setChatSidebarOpen: (open) => set((s) => { s.chatSidebarOpen = open }),
    viewport: 'desktop',
    setViewport: (vp) => set((s) => { s.viewport = vp }),
    previewDeviceChoices: {},
    setPreviewDeviceChoice: (projectId, viewport) =>
      set((s) => {
        if (!projectId) return
        // Re-insert so the newest choice is last, then drop from the front:
        // string keys keep insertion order, so this is a bounded LRU without
        // a second timestamp field to keep in sync.
        const next: Record<string, ViewportSize> = { ...s.previewDeviceChoices }
        delete next[projectId]
        next[projectId] = viewport
        const keys = Object.keys(next)
        for (const stale of keys.slice(0, Math.max(0, keys.length - MAX_PREVIEW_DEVICE_CHOICES))) {
          delete next[stale]
        }
        s.previewDeviceChoices = next
      }),
    canvasView: 'app',
    setCanvasView: (view) => set((s) => { s.canvasView = view }),
    ideActiveTab: 'components',
    setIdeActiveTab: (tab) => set((s) => { s.ideActiveTab = tab }),
    slideOutOpen: false,
    setSlideOutOpen: (open) => set((s) => { s.slideOutOpen = open }),
    selectedElementId: null,
    setSelectedElementId: (id) => set((s) => { s.selectedElementId = id }),
    currentPageId: null,
    setCurrentPageId: (id) => set((s) => { s.currentPageId = id }),
    homeTab: 'my-apps' as HomeTab,
    setHomeTab: (tab) => set((s) => { s.homeTab = tab }),
    previewTarget: 'web' as PreviewTarget,
    setPreviewTarget: (target) => set((s) => { s.previewTarget = target }),
    chatPanelWidth: CHAT_PANEL_DEFAULT_WIDTH,
    inspectorPanelWidth: 360,
    devtoolsPanelHeight: 240,
    setChatPanelWidth: (width) => set((s) => { s.chatPanelWidth = clampChatPanelWidth(width) }),
    setInspectorPanelWidth: (width) => set((s) => { s.inspectorPanelWidth = clamp(width, 240, 640) }),
    setDevtoolsPanelHeight: (height) => set((s) => { s.devtoolsPanelHeight = clamp(height, 120, 600) }),
    publishModalOpen: false,
    setPublishModalOpen: (open) => set((s) => { s.publishModalOpen = open }),
    deployModalOpen: false,
    setDeployModalOpen: (open) => set((s) => { s.deployModalOpen = open }),
  }
}

export function clampChatPanelWidth(width: number): number {
  return clamp(width, CHAT_PANEL_MIN_WIDTH, CHAT_PANEL_MAX_WIDTH)
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}
